import "@/index.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider } from "@/contexts/AuthContext";

import HomePage from "@/pages/HomePage";
import IndustryHubPage from "@/pages/IndustryHubPage";
import { IndustriesIndex } from "@/pages/IndexPages";
import { ModulesIndex, ModuleDetail } from "@/pages/ModulesPages";
import { FrameworksIndex, FrameworkDetail } from "@/pages/FrameworksPages";
import { BriefingsIndex, BriefingDetail } from "@/pages/BriefingsPages";
import { TrackersIndex, TrackerDetail } from "@/pages/TrackersPages";
import LuminousPage from "@/pages/LuminousPage";
import StrategySessionPage from "@/pages/StrategySessionPage";
import { LoginPage, RegisterPage, ForgotPasswordPage, ResetPasswordPage } from "@/pages/AuthPages";
import PricingPage from "@/pages/PricingPage";
import CheckoutSuccessPage from "@/pages/CheckoutSuccessPage";
import { ContactPage, EnterprisePage } from "@/pages/ContactPages";
import { AboutPage, MethodologyPage, TeamPage, PressPage } from "@/pages/CompanyInfoPages";
import IPServicesPage from "@/pages/IPServicesPage";
import MemberDashboard from "@/pages/MemberDashboard";
import SearchPage from "@/pages/SearchPage";
import NotFoundPage from "@/pages/NotFoundPage";
import {
  AdminLayout, AdminOverview, AdminArticles, AdminReports,
  AdminIndustries, AdminCompanies, AdminAuthors,
  AdminEditor, AdminInbox, AdminUsers, AdminPayments,
  AdminNewsletter, AdminFeatured, AdminSettings,
} from "@/pages/Admin";
import { AdminStrategySessions, AdminGeneratedBriefs } from "@/pages/AdminV2";

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Routes>
          {/* Public */}
          <Route path="/" element={<HomePage />} />

          {/* Industries (V3) */}
          <Route path="/industries" element={<IndustriesIndex />} />
          <Route path="/industries/:slug" element={<IndustryHubPage />} />

          {/* Trackers (V3 — replaces articles + research + companies) */}
          <Route path="/trackers" element={<TrackersIndex />} />
          <Route path="/trackers/:slug" element={<TrackerDetail />} />

          {/* Modules + Frameworks + Luminous + Strategy Session */}
          <Route path="/modules" element={<ModulesIndex />} />
          <Route path="/modules/:slug" element={<ModuleDetail />} />
          <Route path="/frameworks" element={<FrameworksIndex />} />
          <Route path="/frameworks/:slug" element={<FrameworkDetail />} />
          <Route path="/luminous" element={<LuminousPage />} />
          <Route path="/strategy-session" element={<StrategySessionPage />} />

          {/* Briefings */}
          <Route path="/briefings" element={<BriefingsIndex />} />
          <Route path="/briefings/:slug" element={<BriefingDetail />} />

          {/* Firm (About) — primary, also /about kept for SEO */}
          <Route path="/firm" element={<AboutPage />} />
          <Route path="/about" element={<Navigate to="/firm" replace />} />
          <Route path="/methodology" element={<MethodologyPage />} />
          <Route path="/team" element={<TeamPage />} />
          <Route path="/press" element={<PressPage />} />
          <Route path="/services/ip" element={<IPServicesPage />} />
          <Route path="/ip-services" element={<Navigate to="/services/ip" replace />} />

          <Route path="/pricing" element={<PricingPage />} />
          <Route path="/contact" element={<ContactPage />} />
          <Route path="/enterprise" element={<EnterprisePage />} />
          <Route path="/search" element={<SearchPage />} />
          <Route path="/checkout/success" element={<CheckoutSuccessPage />} />

          {/* Legacy redirects (V1/V2 → V3) */}
          <Route path="/intelligence" element={<Navigate to="/trackers" replace />} />
          <Route path="/intelligence/:slug" element={<Navigate to="/trackers" replace />} />
          <Route path="/research" element={<Navigate to="/trackers" replace />} />
          <Route path="/research/:slug" element={<Navigate to="/trackers" replace />} />
          <Route path="/companies" element={<Navigate to="/industries" replace />} />
          <Route path="/companies/:slug" element={<Navigate to="/industries" replace />} />
          <Route path="/solutions" element={<Navigate to="/modules" replace />} />
          <Route path="/solutions/:slug" element={<Navigate to="/modules" replace />} />
          <Route path="/copilot" element={<Navigate to="/luminous" replace />} />
          <Route path="/podcasts" element={<Navigate to="/briefings" replace />} />
          <Route path="/podcasts/:slug" element={<Navigate to="/briefings" replace />} />

          {/* Auth */}
          <Route path="/login" element={<LoginPage />} />
          <Route path="/register" element={<RegisterPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />

          {/* Member */}
          <Route path="/dashboard" element={<MemberDashboard />} />

          {/* Admin */}
          <Route path="/admin" element={<AdminLayout />}>
            <Route index element={<AdminOverview />} />
            <Route path="articles" element={<AdminArticles />} />
            <Route path="articles/new" element={<AdminEditor />} />
            <Route path="articles/:slug/edit" element={<AdminEditor />} />
            <Route path="reports" element={<AdminReports />} />
            <Route path="reports/new" element={<AdminEditor />} />
            <Route path="reports/:slug/edit" element={<AdminEditor />} />
            <Route path="industries" element={<AdminIndustries />} />
            <Route path="industries/new" element={<AdminEditor />} />
            <Route path="industries/:slug/edit" element={<AdminEditor />} />
            <Route path="companies" element={<AdminCompanies />} />
            <Route path="companies/new" element={<AdminEditor />} />
            <Route path="companies/:slug/edit" element={<AdminEditor />} />
            <Route path="authors" element={<AdminAuthors />} />
            <Route path="authors/new" element={<AdminEditor />} />
            <Route path="authors/:slug/edit" element={<AdminEditor />} />
            <Route path="inbox" element={<AdminInbox />} />
            <Route path="users" element={<AdminUsers />} />
            <Route path="payments" element={<AdminPayments />} />
            <Route path="newsletter" element={<AdminNewsletter />} />
            <Route path="featured" element={<AdminFeatured />} />
            <Route path="settings" element={<AdminSettings />} />
            <Route path="strategy-sessions" element={<AdminStrategySessions />} />
            <Route path="briefs" element={<AdminGeneratedBriefs />} />
          </Route>

          <Route path="/admin/:collection/new" element={<AdminLayout />}>
            <Route index element={<AdminEditor />} />
          </Route>
          <Route path="/admin/:collection/:slug/edit" element={<AdminLayout />}>
            <Route index element={<AdminEditor />} />
          </Route>

          <Route path="*" element={<NotFoundPage />} />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
